import {
  AddTicketKnowledgeBaseInput,
  BulkAssignTicketInputProps,
  ChangeBillableStatusInput,
  CreateTicketInput,
  CreateTicketResult,
  DeleteTicketAttachmentInput,
  DeleteTicketKnowledgeBaseInput,
  GetAllTicketQueryProps,
  GetAllTicketsResponse,
  GetQueryProps,
  GetTicketByIdResponse,
  GetUnassignedTicketsQueryProps,
  GetUnassignedTicketsResponse,
  MergeTicketInput,
  UpdateAdditionalTicketDetailsInput,
  UpdateScheduleConnectTimeInput,
  UpdateTicketKnowledgeBaseInput,
  UpdateTicketPriorityInput,
  UpdateTicketPriorityResponse,
  UpdateTicketStatusInput,
  UpdateTicketStatusResponse,
} from "shared";
import { axiosClient } from "utilz";
import axiosUploadCall from "utilz/axios-call";
import {
  apiErrorHandler,
  combineDateAndTime,
  combineDateAndTimeV2,
  generateUrlString,
  getMspId,
  getQueryDataObject,
} from "utilz/helpers";

export const getAllTickets = async ({
  perPageCount,
  pageNumber,
  sortColumn,
  sortDirection = "asc",
  filter,
  searchQuery,
  widget,
  timeZone,
  interval,
}: GetAllTicketQueryProps): Promise<GetAllTicketsResponse> => {
  try {
    const data = await getQueryDataObject({
      perPageCount,
      pageNumber,
      sortColumn,
      sortDirection,
      filter,
      searchQuery,
    });
    const url = await generateUrlString({
      baseUrl: "v2/servicedesk/tickets",
      data: {
        ...data,
        ...(widget && { widget }),
        ...(timeZone && { timezone: timeZone }),
        ...(interval && { interval }),
      },
    });
    const response = await axiosClient.get(`${url}`);
    const resData = response.data;
    if (resData.success && resData.statusCode === 200) {
      return resData.data;
    } else {
      throw response;
    }
  } catch (error: any) {
    return apiErrorHandler(error);
  }
};

/** One `filters` predicate, in the tickets-list contract's shape. */
const predicate = (column: string, value: string[]) => ({
  column,
  operator: "=",
  value,
});

// Dispatch portal — unassigned-ticket queue. Lists tickets with no owner/not yet
// assigned. `msp_id` + user come from the token. Pagination (`page`/`per_page`),
// `searchQuery`, and board filtering (via the tickets-list `filters` contract)
// are all honored. See the dispatch portal API validation report.
export const getUnassignedTickets = async ({
  pageNumber,
  perPageCount,
  searchQuery,
  boardIds,
  priorities,
  ticketStatusIds,
  slaStatuses,
  sortColumn,
  sortDirection,
}: GetUnassignedTicketsQueryProps = {}): Promise<
  GetUnassignedTicketsResponse | undefined
> => {
  const filters = [
    ...(boardIds?.length ? [predicate("board", boardIds)] : []),
    ...(priorities?.length ? [predicate("tickets.priority", priorities)] : []),
    ...(ticketStatusIds?.length
      ? [predicate("ticket_status", ticketStatusIds)]
      : []),
    ...(slaStatuses?.length ? [predicate("sla_status", slaStatuses)] : []),
  ];
  try {
    const url = await generateUrlString({
      baseUrl: "v1/servicedesk/unassigned-tickets",
      data: {
        ...(pageNumber && { page: pageNumber.toString() }),
        ...(perPageCount && { per_page: perPageCount.toString() }),
        ...(searchQuery && { searchQuery }),
        // Filters reuse the tickets-list contract: a JSON array of
        // { column, operator, value } predicates (see the tickets filter
        // drawer), AND-ed by the backend.
        //
        // All four columns VERIFIED honoured on UAT 2026-10-01, with the sort and
        // pagination, and multi-predicate AND confirmed (board 1750 ∧ critical 343
        // → 327, every row matching both). An unrecognised column returns 400, so
        // a wrong spelling here fails loudly rather than returning an unfiltered
        // queue. Method + numbers:
        // .claude/projects/control-center/dispatch-portal/dispatch-portal-v2/dp-17/dispatch-unassigned-queue-filter-backend-api-requirements.md §0
        //
        // CAVEAT on `sla_status`: the backend matches it against the resolution
        // clock ALONE, while the UI labels a ticket by the worse of its two clocks
        // (`getWorstStatus`). Where the clocks disagree the filter and the row
        // contradict each other — 3 of 1914 UAT rows. Tracked as Ask 4 in that doc.
        ...(filters.length > 0 && { filters: JSON.stringify(filters) }),
        ...(sortColumn && { sortColumn }),
        ...(sortDirection && { sortDirection }),
      },
    });
    const response = await axiosClient.get(`${url}`);
    const resData = response.data;
    if (resData.success && resData.statusCode === 200) {
      return resData.data;
    } else {
      throw response;
    }
  } catch (error: any) {
    apiErrorHandler(error);
  }
};

// get ticket by id
export const getTicket = async (
  id: string,
): Promise<GetTicketByIdResponse | undefined> => {
  try {
    const url = `/v2/servicedesk/tickets/${id}`;

    const response = await axiosClient.get(`${url}`);
    const resData = response.data;
    if (resData.success && resData.statusCode === 200) {
      return resData.data;
    } else {
      throw response;
    }
  } catch (error: any) {
    apiErrorHandler(error);
  }
};

export const createTicket = async ({
  isBillable,
  checklist,
  board,
  priority,
  status,
  summary,
  description,
  resources,
  urgency,
  workType,
  ticketType,
  ticketSubtype,
  source,
  impact,
  sla,
  customer,
  site,
  ticket_site_id,
  user,
  schedule,
  location,
  generalVariable,
  ticketVariable,
  budgetedHours,
  attachments,
  estimatedEndTime,
  tags,
  contract,
  is_recurring_ticket,
  frequency,
  creation_time,
  start_date,
  end_date,
  time_zone,
  ccMembers,
  privateNote,
}: CreateTicketInput): Promise<CreateTicketResult | undefined> => {
  const data = {
    ticket_summary: summary,
    ticket_description: description,
    board_id: board,
    priority: priority,
    is_billable: isBillable,
    ...(urgency && { urgency: urgency }),
    ...(workType && { work_type_id: workType }),
    ticket_type_id: ticketType,
    ticket_status: status,
    ticket_subtype_id: ticketSubtype,
    source_id: source,
    impact: impact,
    location_id: location,
    site_id: site,
    ticket_site_id: ticket_site_id,
    customer_id: customer,
    customer_user_id: user,
    budgeted_hours: budgetedHours,
    contract_id: contract,
    sla_id: sla,
    ...(generalVariable &&
      generalVariable.length > 0 && {
        general_variable_attributes: generalVariable,
      }),
    ...(ticketVariable &&
      ticketVariable.length > 0 && {
        ticket_variable_attributes: ticketVariable,
      }),
    checklists: checklist,
    attachments: attachments,
    estimated_end_date: estimatedEndTime,
    ...(tags && tags.length > 0 && { tags }),
    ...(ccMembers && ccMembers.length > 0 && { cc_members: ccMembers }),
    ...(schedule && schedule.length > 0 ? { schedulings: schedule } : {}),
    // AAC-577 — posted as a private chat note on the new ticket. Omitted when
    // empty so the create behaves exactly as before.
    ...(privateNote ? { private_note: privateNote } : {}),
    // Recurring-from-tickets-screen flow — backend creates the immediate ticket
    // AND schedules the recurring run in one call when `is_recurring_ticket: true`.
    ...(is_recurring_ticket
      ? {
          is_recurring_ticket: true,
          frequency,
          creation_time,
          start_date,
          end_date,
          time_zone,
        }
      : {}),
  };

  try {
    const response = await axiosClient.post(
      `/servicedesk/create/ticket/${getMspId()}`,
      data,
    );
    const resData = response.data;
    if (resData.success && resData.statusCode === 200) {
      // AAC-577 — the note outcome sits BESIDE `data`, not inside it, and is
      // absent when no note was sent.
      return { ticket: resData.data, privateNote: resData.private_note };
    } else {
      throw response;
    }
  } catch (error: any) {
    apiErrorHandler(error);
  }
};

export const mergeTicket = async ({
  parentTcketId,
  mergeTickets,
}: MergeTicketInput) => {
  try {
    const url = `v2/servicedesk/merge/ticket/${parentTcketId}`;

    const response = await axiosClient.patch(`${url}`, { mergeTickets });
    const resData = response.data;
    if (resData.success && resData.statusCode === 200) {
      return resData.data;
    } else {
      throw response;
    }
  } catch (error: any) {
    apiErrorHandler(error);
  }
};

export const bulkAssignTickets = async ({
  service_id,
  resource_id,
  note,
}: BulkAssignTicketInputProps) => {
  try {
    const url = "/servicedesk/ticket/update/assign/bulk";
    const response = await axiosClient.patch(`${url}`, {
      service_id,
      resource_id,
      note,
    });
    const resp_data = response?.data;
    if (
      resp_data &&
      resp_data?.success === true &&
      resp_data?.statusCode === 200
    ) {
      return resp_data?.data;
    } else {
      throw response;
    }
  } catch (error: any) {
    apiErrorHandler(error);
  }
};

export const bulkModifyTickets = async (data: any) => {
  try {
    const url = "/servicedesk/bulk/ticket_details";
    const response = await axiosClient.patch(`${url}`, data);
    const resp_data = response?.data;
    if (
      resp_data &&
      resp_data?.success === true &&
      resp_data?.statusCode === 200
    ) {
      return resp_data?.data;
    } else {
      throw response;
    }
  } catch (error: any) {
    apiErrorHandler(error);
  }
};

export const changeBillableStatus = async ({
  ticketId,
  newValue,
}: ChangeBillableStatusInput) => {
  try {
    const url = `/servicedesk/update/ticket/${ticketId}/is_billable/${newValue}`;

    const response = await axiosClient.patch(`${url}`);
    const resData = response.data;
    if (resData.success && resData.statusCode === 200) {
      return resData.data;
    } else {
      throw response;
    }
  } catch (error: any) {
    apiErrorHandler(error);
  }
};

export const updateTicketStatus = async ({
  ticketId,
  statusId,
}: UpdateTicketStatusInput): Promise<UpdateTicketStatusResponse> => {
  try {
    const url = `/servicedesk/update/ticket/${ticketId}/status/${statusId}`;

    const response = await axiosClient.patch(`${url}`);
    const resData = response.data;
    if (resData.success && resData.statusCode === 200) {
      return resData.data;
    } else {
      throw response;
    }
  } catch (error: any) {
    return apiErrorHandler(error);
  }
};

export const updateTicketPriority = async ({
  ticketId,
  priority,
}: UpdateTicketPriorityInput): Promise<UpdateTicketPriorityResponse> => {
  try {
    const url = `/servicedesk/update/ticket/${ticketId}/proirity/${priority}`;

    const response = await axiosClient.patch(`${url}`);
    const resData = response.data;
    if (resData.success && resData.statusCode === 200) {
      return resData.data;
    } else {
      throw response;
    }
  } catch (error: any) {
    return apiErrorHandler(error);
  }
};

export const uploadTicketAttachments = ({
  formData,
  serviceType = "ticket",
  attachmentType = "general",
}: {
  formData: FormData;
  serviceType: "ticket" | "project" | "task";
  attachmentType: "general" | "notes";
}) =>
  axiosUploadCall(
    `/v2/servicedesk/attachments/${serviceType}/${attachmentType}`,
    formData,
  );
export const deleteTicketAttachments = async ({
  attachmentId,
}: DeleteTicketAttachmentInput) => {
  const data = {
    attachment_ids: attachmentId,
  };
  try {
    const url = "/servicedesk/delete/ticket/attachments";
    const response = await axiosClient.delete(`${url}`, { data });
    const resData = response?.data;
    if (resData && resData.success === true && resData.statusCode === 200) {
      return response.data;
    } else {
      throw response;
    }
  } catch (error: any) {
    apiErrorHandler(error);
  }
};

export const updateTicketDetails = async ({
  ticketSummary,
  ticketDescription,
  ticketId,
  workType,
  workRole,
  source,
  budgetedHours,
  estimatedEndDate,
  ticketType,
  ticketSubtype,
  location,
  ticketVariableAttribute,
  generalVariableAttribute,
  tags,
  ticketOwner,
  isSlaExpired,
  customerId,
  siteId,
  userId,
  board,
  urgency,
  impact,
  sla,
  attachments,
  ticketSiteId,
  deviceId,
  privateNote,
}: UpdateAdditionalTicketDetailsInput) => {
  try {
    const data = {
      ...(ticketSiteId && ticketSiteId !== null
        ? { ticket_site_id: ticketSiteId }
        : {}),
      ...(ticketSummary && ticketSummary !== null
        ? { ticket_summary: ticketSummary }
        : {}),
      ...(ticketDescription && ticketDescription !== null
        ? { ticket_description: ticketDescription }
        : {}),
      ...(ticketOwner && ticketOwner !== null
        ? { ticket_owner: ticketOwner }
        : {}),
      ...(workType && workType !== null ? { work_type: workType } : {}),
      ...(workRole && workRole !== null ? { work_role: workRole } : {}),
      ...(source && source !== null ? { source: source } : {}),
      ...(location && location !== null ? { location: location } : {}),
      ...(budgetedHours && budgetedHours !== null
        ? { budgeted_hours: budgetedHours }
        : {}),
      ...(estimatedEndDate && estimatedEndDate !== null
        ? { estimated_end_date: estimatedEndDate }
        : {}),
      ...(ticketType && ticketType !== null
        ? { ticket_type_id: ticketType }
        : {}),
      ...(ticketSubtype && ticketSubtype !== null
        ? { ticket_subtype_id: ticketSubtype }
        : {}),
      ...(ticketVariableAttribute && ticketVariableAttribute !== null
        ? { ticket_variable_attributes: ticketVariableAttribute }
        : {}),
      ...(generalVariableAttribute && generalVariableAttribute !== null
        ? { general_variable_attributes: generalVariableAttribute }
        : {}),
      ...(tags && tags !== null ? { tags: tags } : {}),
      ...(isSlaExpired && isSlaExpired !== null
        ? { is_expired_sla: isSlaExpired }
        : {}),
      ...(customerId && customerId !== null ? { customer_id: customerId } : {}),
      ...(siteId && siteId !== null ? { site_id: siteId } : {}),
      ...(userId && userId !== null ? { customer_user_id: userId } : {}),
      ...(board && board !== null ? { board: board } : {}),
      ...(urgency && urgency !== null ? { urgency: urgency } : {}),
      ...(impact && impact !== null ? { impact: impact } : {}),
      ...(sla && sla !== null ? { sla: sla } : {}),
      ...(attachments && attachments !== null
        ? { attachments: attachments }
        : {}),
      ...(deviceId !== undefined ? { device_id: deviceId || null } : {}),
      ...(privateNote ? { private_note: privateNote } : {}),
    };

    if (Object.entries(data).length <= 0) {
      return "";
    }

    const url = `/servicedesk/update/ticket_details/${ticketId}`;

    const response = await axiosClient.patch(`${url}`, data);
    const resData = response?.data;
    if (resData && resData.success === true && resData.statusCode === 200) {
      return response.data;
    } else {
      throw response;
    }
  } catch (error: any) {
    apiErrorHandler(error);
  }
};

export const updateScheduleConnectTime = async ({
  connectTimeFrom,
  connectTimeTo,
}: UpdateScheduleConnectTimeInput) => {
  try {
    const data = {
      connect_time_from: connectTimeFrom,
      connect_time_to: connectTimeTo,
    };

    const url = "/servicedesk/create/schedule/:msp_id/:ticket_id";
    const response = await axiosClient.post(url, data);

    const resData = response?.data;
    if (resData && resData.success === true && resData.statusCode === 200) {
      return response.data;
    } else {
      throw response;
    }
  } catch (error: any) {
    apiErrorHandler(error);
  }
};

export const getTicketQaStatus = async (ticketId: string) => {
  try {
    const url = `${
      import.meta.env["VITE_APP_QA_BASE_URL"]
    }/qa_board/ticket/qa_health?service_id=${ticketId}`;
    const response = await axiosClient.get(url);
    const resData = response?.data;
    if (resData.success && resData.statusCode === 200) {
      return resData.data;
    }
    throw response;
  } catch (error: any) {
    apiErrorHandler(error);
  }
};

export const addProductToTicket = async (data: any) => {
  try {
    const url = `/v1/products/${data.service_type}/${data.service_id}`;
    const response = await axiosClient.post(url, data?.productAndExpenseData);
    const resData = response?.data;
    if (resData && resData.success === true && resData.statusCode === 200) {
      return response.data;
    } else {
      throw response;
    }
  } catch (error: any) {
    apiErrorHandler(error);
  }
};

export const addExpenseToTicket = async (data: any) => {
  try {
    const url = `/v1/expense/${data.service_type}/${data.service_id}`;
    const response = await axiosClient.post(url, data?.productAndExpenseData);
    const resData = response?.data;
    if (resData && resData.success === true && resData.statusCode === 200) {
      return response.data;
    } else {
      throw response;
    }
  } catch (error: any) {
    apiErrorHandler(error);
  }
};

export const addTicketKnowledgeBase = async ({
  ticketId,
  knowledgeBase,
}: AddTicketKnowledgeBaseInput) => {
  try {
    const url = `/v1/servicedesk/ticket/${ticketId}/knowledge-base`;

    const response = await axiosClient.post(`${url}`, {
      ...knowledgeBase,
    });
    const resData = response.data;
    if (resData.success && resData.statusCode === 200) {
      return resData.data;
    } else {
      throw response;
    }
  } catch (error: any) {
    apiErrorHandler(error);
  }
};

export const updateTicketKnowledgeBase = async ({
  ticketId,
  knowledgeBase,
}: UpdateTicketKnowledgeBaseInput) => {
  try {
    const url = `/v1/servicedesk/ticket/${ticketId}/knowledge-base`;

    const response = await axiosClient.patch(`${url}`, {
      ...knowledgeBase,
    });
    const resData = response.data;
    if (resData.success && resData.statusCode === 200) {
      return resData.data;
    } else {
      throw response;
    }
  } catch (error: any) {
    apiErrorHandler(error);
  }
};

export const deleteTicketKnowledgeBase = async ({
  ticketId,
  knowledgeBaseId,
}: DeleteTicketKnowledgeBaseInput) => {
  try {
    const url = `/v1/servicedesk/ticket/${ticketId}/knowledge-base/${knowledgeBaseId}`;

    const response = await axiosClient.delete(`${url}`);
    const resData = response.data;
    if (resData.success && resData.statusCode === 200) {
      return resData.data;
    } else {
      throw response;
    }
  } catch (error: any) {
    apiErrorHandler(error);
  }
};

export const getTicketCountByBoard = async () => {
  try {
    const url = "/v1/servicedesk/tickets/boards/count";
    const response = await axiosClient.get(url);
    const resData = response.data;
    if (resData.success && resData.statusCode === 200) {
      return resData.data;
    } else {
      throw response;
    }
  } catch (error: any) {
    apiErrorHandler(error);
  }
};

export const requestTicketFeedback = async (ticketId: string) => {
  try {
    const url = `/servicedesk/request/feedback/${ticketId}`;
    const response = await axiosClient.post(url);
    const resData = response.data;
    if (resData.success && resData.statusCode === 200) {
      return resData.data;
    } else {
      throw response;
    }
  } catch (error: any) {
    apiErrorHandler(error);
  }
};
